import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Icons, ImpBackdrop, ImpButton, ImpContainer, ImpDivider, ImpFlex, ImpIcon, ImpImage, ImpPad, ImpSurface, ImpText, colors, zIndex, } from '../..';
import { fadeIn, fadeOut } from '../../DesignSystem/design-system';
const CLOSE_DURATION = 100;
const OPEN_DURATION = 300;
const MODAL_MIN_HEIGHT = '280px';
const MODAL_NAVBAR_HEIGHT = '56px';
const ModalContentWrapper = styled(ImpContainer) `
  z-index: ${zIndex.modalContent};
  animation: ${({ isClosing }) => (isClosing ? fadeOut : fadeIn)}
    ${({ isClosing }) => isClosing ? `${CLOSE_DURATION}ms` : `${OPEN_DURATION}ms`}
    ease-in-out;
`;
const HeaderContentWrapper = styled(ImpContainer) `
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
`;
const HeaderIconWrapper = styled(ImpContainer) `
  ${({ hasHeaderContent }) => hasHeaderContent &&
    `
    margin-left: auto;
  `}
`;
const ImpFlexRelative = styled(ImpFlex) `
  position: relative;
`;
const HeaderContainer = styled(ImpContainer) `
  ${({ sticky }) => sticky &&
    `
    z-index: 1;
    background-color: ${colors.surface.surfaceMid};
    position: sticky;
    top: 0px;
    background-clip: padding-box;
  `}
`;
export const ImpModalHeader = ({ closeModal, invertColor, headerImg, headerImgHeight, headerText, sticky = false, preventClose = false, }) => {
    const hasHeaderContent = Boolean(headerImg || headerText);
    const getCloseIconStroke = () => {
        if (preventClose)
            return colors.content.onBackgroundDisabled;
        return invertColor ? 'white' : undefined;
    };
    return (React.createElement(HeaderContainer, { height: MODAL_NAVBAR_HEIGHT, width: "100%", dataTestId: "modal-header", sticky: sticky },
        React.createElement(ImpFlex, { direction: "column", fluid: true },
            React.createElement(ImpFlexRelative, { fluid: true, align: "center", justify: hasHeaderContent ? 'space-between' : 'flex-end' },
                hasHeaderContent && (React.createElement(HeaderContentWrapper, null,
                    headerImg && (React.createElement(ImpImage, { src: headerImg, alt: "header logo image", height: headerImgHeight })),
                    headerText && (React.createElement(ImpText, { typography: "label1" }, headerText)))),
                React.createElement(HeaderIconWrapper, { hasHeaderContent: hasHeaderContent },
                    React.createElement(ImpPad, { padding: "16px 16px" },
                        React.createElement(ImpButton, { variant: "wrapper", state: preventClose ? 'disabled' : undefined, onClick: closeModal },
                            React.createElement(ImpIcon, { hover: preventClose ? undefined : !invertColor, iconSrc: Icons.close, stroke: getCloseIconStroke(), size: "24px" })))))),
        sticky && React.createElement(ImpDivider, { color: colors.border.borderVariant })));
};
const ContentContainer = styled(ImpContainer) `
  overflow-y: auto;
  display: flex;
  min-height: ${(props) => props.respectContentHeight ? 'auto' : MODAL_MIN_HEIGHT};
  max-height: calc(100dvh - 32px);
  flex-grow: 1;
  height: 100%;
`;
const SurfaceNoOverflow = styled(ImpSurface) `
  overflow: hidden;
`;
export const useModal = (initialState) => {
    const [isOpen, setIsOpen] = useState(initialState);
    const [isClosing, setIsClosing] = useState(false);
    const openModal = () => {
        setIsClosing(false);
        setIsOpen(true);
    };
    const closeModal = () => {
        setIsClosing(true);
        setTimeout(() => {
            setIsClosing(false);
            setIsOpen(false);
        }, CLOSE_DURATION);
    };
    return [isOpen, isClosing, openModal, closeModal];
};
// this fixes the scrollbar jumping when the backdrop is opened
const getScrollbarWidth = () => {
    return window.innerWidth - document.documentElement.clientWidth;
};
export const useDocumentOverflow = (isOpen) => {
    useEffect(() => {
        const scrollbarWidth = getScrollbarWidth();
        if (isOpen) {
            document.documentElement.style.overflow = 'hidden';
            document.documentElement.style.marginRight = `${scrollbarWidth}px`;
        }
        else {
            document.documentElement.style.overflow = 'unset';
            document.documentElement.style.marginRight = 'unset';
        }
        return () => {
            document.documentElement.style.overflow = 'unset';
            document.documentElement.style.marginRight = 'unset';
        };
    }, [isOpen]);
};
export default function ImpModal({ children, isOpen, isClosing, respectContentWidth = false, respectContentHeight = false, closeModal, onBackgroundClick, headerImg, headerImgHeight = '24px', headerText, stickyHeader = false, preventClose = false, }) {
    useDocumentOverflow(isOpen || isClosing);
    return (React.createElement(ImpBackdrop, { isOpen: isOpen, isClosing: isClosing, onClick: onBackgroundClick }, isOpen && (React.createElement(ModalContentWrapper, { isClosing: isClosing, dataTestId: "modal-content" },
        React.createElement(SurfaceNoOverflow, { hasBorderRadius: true, elevation: 5, dataTestId: "modal-surface" },
            React.createElement(ContentContainer, { dataTestId: "modal-surface-container", respectContentHeight: respectContentHeight, width: respectContentWidth ? undefined : 'min(600px, 100vw - 32px)' },
                React.createElement(ImpFlex, { direction: "column", fluid: true },
                    closeModal && (React.createElement(ImpModalHeader, { closeModal: closeModal, headerImg: headerImg, headerImgHeight: headerImgHeight, headerText: headerText, sticky: stickyHeader, preventClose: preventClose })),
                    children)))))));
}
//# sourceMappingURL=ImpModal.js.map